/** @file API logic for a structure's saved fee templates */

import { createDefaultHeaders, getJSONOrThrow, throwIfError } from '../api';
import { getSession, urlWithAdminQuery } from '../auth';

export type FeeTemplateType = 'SPLIT' | 'FLAT' | 'UNIT_FLAT';

export type FeeTemplate = {
  id: string;
  structure_id: string;
  type: FeeTemplateType;
  description: string;
  cost: number;
};

/**
 * List the saved fee templates for a structure
 *
 * @param {string} structureId - UUID of the structure
 * @returns {Promise<FeeTemplate[]>} - The structure's saved fee templates
 */
export const listFeeTemplates = (structureId: string): Promise<FeeTemplate[]> =>
  getSession().then((session) =>
    fetch(urlWithAdminQuery(`/structure/${structureId}/fee-templates`, session.adminEmail), {
      method: 'GET',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(getJSONOrThrow)
      .then((body) => body.fee_templates as FeeTemplate[]),
  );

/**
 * Save a fee template for a structure. Saving a fee whose type and description
 * already exist on the structure updates that template's cost instead of
 * creating a duplicate.
 *
 * @param {string} structureId - UUID of the structure
 * @param {object} template - The fee to save (type, description, cost)
 * @returns {Promise<FeeTemplate>} - The saved template
 */
export const saveFeeTemplate = (
  structureId: string,
  template: { type: FeeTemplateType; description: string; cost: number },
): Promise<FeeTemplate> =>
  getSession().then((session) =>
    fetch(urlWithAdminQuery(`/structure/${structureId}/fee-templates`, session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify(template),
    }).then(getJSONOrThrow),
  );

/**
 * Delete a saved fee template from a structure
 *
 * @param {string} structureId - UUID of the structure
 * @param {string} templateId - UUID of the template to delete
 * @returns {Promise<Response>} - The delete response
 */
export const deleteFeeTemplate = (structureId: string, templateId: string): Promise<Response> =>
  getSession().then((session) =>
    fetch(
      urlWithAdminQuery(
        `/structure/${structureId}/fee-templates/${templateId}`,
        session.adminEmail,
      ),
      {
        method: 'DELETE',
        headers: createDefaultHeaders({
          authToken: session.idToken.toString(),
        }),
      },
    ).then(throwIfError),
  );
